/**
 * The suite product logos, in one place.
 *
 * These were private to the Roles tab until Settings › Notifications adopted the same product tab
 * strip. Two copies of the same four URLs is how the two screens drift apart, so the map lives here
 * and both import it.
 *
 * Keys are the RBAC product ids (`4comply`, `4preferences`, `4bridge`, `4segments`), which is what
 * the permission catalog uses. The Notification Center's own registry keys its products differently
 * — `comply`, `preferences`, `bridge`, `segments` — so it goes through `productLogoFor`, which
 * accepts either spelling.
 *
 * The URLs are same-origin paths into `apps/fourcomply-dashboard/public/logos/`. They were remote
 * URLs on the marketing site until 2026-09-21, when a customer on a European corporate network got
 * a 403 for every one of them and logged into a dashboard with no logos. `product-logos.spec.ts`
 * fails if an absolute URL comes back. A caller must still handle the image failing to load,
 * because the tab has no visible label while the logo is showing.
 */
export const PRODUCT_LOGOS: Readonly<Record<string, string>> = {
  '4bridge': '/logos/4bridge.svg',
  '4comply': '/logos/4comply.svg',
  '4preferences': '/logos/4preferences.svg',
  '4segments': '/logos/4segments.svg',
};

/**
 * The logo for a product id in either spelling, or `undefined` when there is none.
 *
 * `comply` and `4comply` are the same product wearing the two names the platform gives it: the RBAC
 * permission catalog uses the `4`-prefixed form, the Notification Center product registry does not.
 * Normalising here keeps that mismatch out of every template.
 */
export function productLogoFor(productId: string): string | undefined {
  if (!productId) {
    return undefined;
  }
  const key: string = productId.toLowerCase();
  // Own-property lookups only. The Notification Center's product keys come from the API, and a
  // plain `PRODUCT_LOGOS[key]` would answer `constructor` or `__proto__` with an inherited member
  // of Object.prototype — typed as string, and on its way into an [src] binding. Not an XSS (the
  // URL sanitizer still runs, and the value is never attacker-controlled text), but a broken image
  // and a spurious request are not worth the two lines it costs to make impossible.
  return own(key) ?? own(`4${key}`);
}

function own(key: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(PRODUCT_LOGOS, key) ? PRODUCT_LOGOS[key] : undefined;
}
